import React from 'react'
import {useIntl} from 'react-intl'

import {TranscriptLine} from '../hooks/useVoiceCallState'

const parseMarkdownText = (text: string): React.ReactNode => {
  if (!text) return null

  const lines = text.split('\n')
  const elements: React.ReactNode[] = []

  lines.forEach((line, lineIndex) => {
    const trimmedLine = line.trim()

    if (trimmedLine.startsWith('###')) {
      const headerText = trimmedLine.replace(/^###\s*/, '')
      elements.push(
        <div key={`h3-${lineIndex}`} className='voice-transcript-header'>
          {parseInlineMarkdown(headerText)}
        </div>
      )
      return
    }

    if (trimmedLine.startsWith('##')) {
      const headerText = trimmedLine.replace(/^##\s*/, '')
      elements.push(
        <div key={`h2-${lineIndex}`} className='voice-transcript-header voice-transcript-h2'>
          {parseInlineMarkdown(headerText)}
        </div>
      )
      return
    }

    if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      const bulletText = trimmedLine.replace(/^[-*]\s*/, '')
      elements.push(
        <div key={`bullet-${lineIndex}`} className='voice-transcript-bullet'>
          <span className='voice-transcript-bullet-dot'>•</span>
          <span>{parseInlineMarkdown(bulletText)}</span>
        </div>
      )
      return
    }

    const numberedMatch = trimmedLine.match(/^(\d+)\.\s*(.*)/)
    if (numberedMatch) {
      elements.push(
        <div key={`num-${lineIndex}`} className='voice-transcript-numbered'>
          <span className='voice-transcript-number'>{numberedMatch[1]}.</span>
          <span>{parseInlineMarkdown(numberedMatch[2])}</span>
        </div>
      )
      return
    }

    if (trimmedLine === '---' || trimmedLine === '***') {
      elements.push(<hr key={`hr-${lineIndex}`} className='voice-transcript-hr' />)
      return
    }

    if (trimmedLine) {
      elements.push(
        <span key={`p-${lineIndex}`}>
          {parseInlineMarkdown(trimmedLine)}
          {lineIndex < lines.length - 1 ? ' ' : ''}
        </span>
      )
    }
  })

  return <>{elements}</>
}

const parseInlineMarkdown = (text: string): React.ReactNode => {
  if (!text) return null
  const regex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)/g

  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let keyIndex = 0

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    if (match[1]) {
      parts.push(
        <strong key={`bold-${keyIndex++}`} className='voice-transcript-bold'>
          {match[2]}
        </strong>
      )
    } else if (match[3]) {
      parts.push(
        <em key={`italic-${keyIndex++}`} className='voice-transcript-italic'>
          {match[4]}
        </em>
      )
    } else if (match[5]) {
      parts.push(
        <code key={`code-${keyIndex++}`} className='voice-transcript-code'>
          {match[6]}
        </code>
      )
    }

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? <>{parts}</> : text
}

interface VoiceTranscriptionPanelProps {
  transcript: TranscriptLine[]
  interimUserTranscript: string
  status: string
}

const VoiceTranscriptionPanel: React.FC<VoiceTranscriptionPanelProps> = ({
  transcript,
  interimUserTranscript,
  status,
}) => {
  const intl = useIntl()
  const chatContainerRef = React.useRef<HTMLDivElement>(null)

  const hasContent = transcript.length > 0 || interimUserTranscript.trim()

  React.useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [transcript, interimUserTranscript])

  return (
    <div
      className='voice-chat-container'
      ref={chatContainerRef}
      style={{
        background: 'transparent',
        boxShadow: 'none',
        padding: '12px 16px',
      }}
    >
      {hasContent ? (
        <>
          {transcript.map((line) => (
            <div key={line.id} className={`voice-message-wrapper ${line.speaker}`}>
              {line.speaker === 'ai' && (
                <div className='voice-message-icon bot'>
                  <i className='bi bi-robot'></i>
                </div>
              )}
              <div
                className='voice-message-bubble'
                style={
                  line.speaker === 'ai'
                    ? {
                        background: 'rgba(245,250,255,0.95)',
                        color: '#0f172a',
                        boxShadow: '0 12px 30px rgba(12,48,90,0.06)',
                        borderRadius: 12,
                        padding: '10px 14px',
                      }
                    : {
                        background: '#2f3e4a',
                        color: '#fff',
                        boxShadow: '0 10px 26px rgba(15,23,42,0.12)',
                        borderRadius: 12,
                        padding: '10px 14px',
                      }
                }
              >
                {line.speaker === 'ai' ? parseMarkdownText(line.text) : line.text}
              </div>
              {line.speaker === 'user' && (
                <div className='voice-message-icon user'>
                  <i className='bi bi-person-fill'></i>
                </div>
              )}
            </div>
          ))}

          {interimUserTranscript && status === 'listening' && (
            <div className='voice-message-wrapper user interim'>
              <div
                className='voice-message-bubble'
                style={{
                  background: '#2f3e4a',
                  color: '#fff',
                  boxShadow: '0 10px 26px rgba(15,23,42,0.12)',
                  borderRadius: 12,
                  padding: '10px 14px',
                }}
              >
                {/* Deliberately not the recogniser's text: it is locked to the UI language and
                    mis-decodes anything else. The server's transcription replaces this. */}
                {intl.formatMessage({id: 'CHAT.VOICE.LISTENING'})}
              </div>
              <div className='voice-message-icon user'>
                <i className='bi bi-person-fill'></i>
              </div>
            </div>
          )}

          {status === 'responding' && (
            <div className='voice-message-wrapper bot'>
              <div className='voice-message-icon bot'>
                <i className='bi bi-robot'></i>
              </div>
              <div
                className='voice-message-bubble'
                style={{
                  background: 'rgba(245,250,255,0.95)',
                  color: '#0f172a',
                  boxShadow: '0 12px 30px rgba(12,48,90,0.06)',
                  borderRadius: 12,
                  padding: '10px 14px',
                }}
              >
                <div className='voice-typing-indicator' style={{display: 'flex', gap: 6}}>
                  <div
                    className='voice-typing-dot'
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'rgba(15,23,42,0.12)',
                    }}
                  ></div>
                  <div
                    className='voice-typing-dot'
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'rgba(15,23,42,0.12)',
                    }}
                  ></div>
                  <div
                    className='voice-typing-dot'
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'rgba(15,23,42,0.12)',
                    }}
                  ></div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className='voice-chat-empty' style={{padding: '16px'}}>
          {intl.formatMessage({id: 'CHAT.VOICE.WAITING_FOR_SPEECH'})}
        </div>
      )}
    </div>
  )
}

export default VoiceTranscriptionPanel
